import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';
import Logo from '../components/Logo';
import { Gift, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ensureOneSignalLogin } from '../utils/oneSignalInit';
import {
  normalizePhone,
  validateEmail,
  validateOtpCode,
  validatePersonName,
  validateReferralCode
} from '../utils/inputValidation';

const CODE_LENGTH = 4;
const CODE_TIME = 300; // 5 minutos

const VerifyCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useApp();

  const phoneFromState = location.state?.phone || localStorage.getItem('pendingPhone');
  const [phone] = useState(phoneFromState);

  const [codeDigits, setCodeDigits] = useState(Array(CODE_LENGTH).fill(''));
  const inputsRef = useRef([]);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState('');

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 🔥 ESTADO DE INTENTOS
  const [attempts, setAttempts] = useState(0);

  const [secondsLeft, setSecondsLeft] = useState(CODE_TIME);
  const timerRef = useRef(null);
  const [codeVerified, setCodeVerified] = useState(false);

  const codeInput = codeDigits.join('');

  useEffect(() => {
    if (!phone) {
      navigate('/register');
      return;
    }
    localStorage.setItem('pendingPhone', phone);
  }, [phone, navigate]);

  useEffect(() => {
    if (secondsLeft > 0 && !codeVerified) {
      timerRef.current = setTimeout(() => setSecondsLeft(s => s - 1), 1000);
    }
    return () => clearTimeout(timerRef.current);
  }, [secondsLeft, codeVerified]);

  useEffect(() => {
    if (codeInput.length === CODE_LENGTH && !codeVerified && !loading) {
      handleVerifyCode(codeInput);
    }
  }, [codeInput]); // eslint-disable-line

  const handleDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...codeDigits];
    updated[index] = value;
    setCodeDigits(updated);
    if (value && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = e => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    const newDigits = pasted.split('');
    while (newDigits.length < CODE_LENGTH) newDigits.push('');
    setCodeDigits(newDigits);
  };

  const handleVerifyCode = async finalCode => {
    setLoading(true);
    setMessage('');
    try {
      const { data, error } = await supabase.rpc('verify_registration_otp', {
        p_phone: normalizePhone(phone),
        p_code: validateOtpCode(finalCode)
      });

      if (error) throw error;

      const result = typeof data === 'string' ? JSON.parse(data) : data;

      if (result.error === 'not_found') {
        setMessage('Sesión expirada o código no encontrado. Intenta de nuevo.');
        setMessageType('error');
        setTimeout(() => navigate('/register'), 2000);
        return;
      }

      if (result.error === 'expired') {
        setMessage('El código ha expirado.');
        setMessageType('error');
        resetCode();
        return;
      }

      if (result.error === 'locked') {
        setMessage('Demasiados intentos fallidos. Por seguridad, cancelamos el proceso.');
        setMessageType('error');
        setTimeout(() => navigate('/register'), 3000);
        return;
      }

      if (result.error === 'invalid') {
        const newAttempts = 3 - (result.attempts_left ?? 0);
        setAttempts(newAttempts);
        setMessage(`Código incorrecto. Te quedan ${result.attempts_left ?? 0} intentos.`);
        setMessageType('error');
        resetCode();
        return;
      }

      setMessage('');
      setCodeVerified(true);
      clearTimeout(timerRef.current);
    } catch (err) {
      console.error(err);
      setMessage('Error al verificar código.');
      setMessageType('error');
      resetCode();
    } finally {
      setLoading(false);
    }
  };

  const resetCode = () => {
    setCodeDigits(Array(CODE_LENGTH).fill(''));
    inputsRef.current[0]?.focus();
  };

  const formatTime = (seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const handleResendCode = async () => {
    // 🔥 CAPA 2 ANTI-SPAM
    const localAttempts = parseInt(localStorage.getItem('otp_attempts') || '0');
    if (localAttempts >= 3) {
      setMessage('Límite máximo de códigos superado por hoy.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const { error: otpError } = await supabase.rpc('resend_registration_otp', {
        p_phone: normalizePhone(phone)
      });

      if (otpError) {
        if (otpError.message?.includes('Límite de seguridad') || otpError.code === 'P0001') {
          localStorage.setItem('otp_attempts', '3');
          throw new Error('Has superado el límite diario de códigos para este número.');
        }
        throw otpError;
      }

      localStorage.setItem('otp_attempts', (localAttempts + 1).toString());
      setSecondsLeft(CODE_TIME);
      setAttempts(0); // Reinicia intentos manuales
      setMessage('Código reenviado correctamente.');
      setMessageType('success');
      resetCode();
    } catch (err) {
      console.error(err);
      setMessage(err.message || 'Error al reenviar el código.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setMessage('Nombre y apellido son obligatorios.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    try {
      const rawPassword = localStorage.getItem('rawPasswordForLogin'); 
      
      if (!rawPassword) {
        setMessage('Sesión expirada. Regístrate nuevamente.');
        setMessageType('error');
        return;
      }

      const safePhone = normalizePhone(phone);
      const safeFirstName = validatePersonName(firstName, 'Nombre');
      const safeLastName = validatePersonName(lastName, 'Apellido');
      const safeEmail = validateEmail(email);
      const safeReferralCode = validateReferralCode(referralCodeInput);

      if (safeReferralCode) {
        const { data: referrerId, error: refError } = await supabase.rpc('validate_referral_code', {
          p_code: safeReferralCode
        });

        if (refError) throw refError;
        if (!referrerId) {
          setMessage('El código de referido no es válido.');
          setMessageType('error');
          setLoading(false);
          return;
        }
      }

      const { error: userError } = await supabase.rpc('complete_registration', {
        p_phone: safePhone,
        p_first_name: safeFirstName,
        p_last_name: safeLastName,
        p_email: safeEmail,
        p_referral_code: safeReferralCode
      });

      if (userError) throw userError;

      const loggedUser = await login(safePhone, rawPassword);
      
      if (!loggedUser) {
          throw new Error("La cuenta se creó, pero falló el inicio de sesión automático.");
      }

      if (window.OneSignal) {
        ensureOneSignalLogin(loggedUser.id, { requirePermission: true }).catch((err) =>
          console.warn('OneSignal login:', err)
        );
      }

      localStorage.removeItem('pendingPassword');
      localStorage.removeItem('pendingPhone');
      localStorage.removeItem('rawPasswordForLogin');

      navigate('/home');
      
    } catch (err) {
      console.error(err);
      setMessage(err.message || 'Error al completar registro.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout showNavigation={false}>
      <div className="min-h-screen px-6 py-12 bg-gray-50 flex flex-col justify-center">
        <motion.div className="max-w-md mx-auto w-full" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="text-center mb-10">
            <Logo size="large" className="mx-auto mb-8" />
          </div>

          {!codeVerified ? (
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-center mb-3 text-gray-800">Verifica tu número</h2>
              <p className="text-center text-gray-600 mb-6 leading-relaxed">
                Ingresa el código de 4 dígitos enviado por WhatsApp al<br />
                <strong className="text-lg">{phone}</strong>
              </p>

              <div className="flex justify-center gap-4 mb-8">
                {codeDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => (inputsRef.current[i] = el)}
                    type="text"
                    value={digit}
                    onChange={e => handleDigitChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    maxLength={1}
                    className="w-16 h-16 text-3xl font-bold text-center border-2 border-gray-300 rounded-2xl focus:border-blue-900 focus:outline-none transition-all duration-200"
                    disabled={loading || attempts >= 3}
                  />
                ))}
              </div>

              {message && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`text-center font-bold mb-6 ${messageType === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                  {message}
                </motion.p>
              )}

              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600 font-medium">{formatTime(secondsLeft)}</span>
                {secondsLeft === 0 && attempts < 3 && (
                  <button onClick={handleResendCode} disabled={loading} className="text-blue-900 font-semibold underline hover:text-blue-700 disabled:text-gray-400 disabled:no-underline transition-colors">
                    Reenviar código
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-[32px] shadow-sm border border-slate-100 p-8 relative overflow-hidden">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">👋</div>
                <h2 className="text-2xl font-black text-slate-800 tracking-tight">Casi listo</h2>
                <p className="text-slate-500 font-medium text-sm mt-1">Completa tus datos para empezar</p>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input placeholder="Nombre" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-5 py-4 text-base font-medium border border-slate-200 rounded-2xl focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all bg-slate-50 focus:bg-white" />
                  <input placeholder="Apellido" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-5 py-4 text-base font-medium border border-slate-200 rounded-2xl focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all bg-slate-50 focus:bg-white" />
                </div>
                
                <input placeholder="Correo electrónico (opcional)" value={email} onChange={e => setEmail(e.target.value)} type="email" className="w-full px-5 py-4 text-base font-medium border border-slate-200 rounded-2xl focus:border-blue-600 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all bg-slate-50 focus:bg-white" />

                <div className="relative mt-6 pt-6 border-t border-dashed border-slate-200">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-indigo-400" /> ¿Te invitó un amigo?
                  </label>
                  <input 
                    placeholder="Código de referido (Opcional)" 
                    value={referralCodeInput} 
                    onChange={e => setReferralCodeInput(e.target.value.toUpperCase())} 
                    type="text" 
                    className="w-full px-5 py-4 text-base font-bold text-center tracking-widest uppercase border border-slate-200 rounded-2xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all bg-slate-50 focus:bg-white placeholder:font-medium placeholder:tracking-normal placeholder:normal-case placeholder:text-left" 
                  />
                </div>

                {message && (
                  <motion.p initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center text-red-600 font-bold text-sm pt-2">
                    {message}
                  </motion.p>
                )}

                <button 
                  onClick={handleCompleteRegistration} 
                  disabled={loading} 
                  className="w-full mt-8 bg-blue-600 hover:bg-blue-700 text-white font-black py-4 rounded-2xl transition-all duration-300 disabled:bg-slate-300 disabled:text-slate-500 shadow-[0_10px_20px_rgba(37,99,235,0.2)] hover:shadow-[0_15px_25px_rgba(37,99,235,0.3)] disabled:shadow-none active:scale-[0.98]"
                >
                  {loading ? 'Creando cuenta...' : 'Completar Registro'}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default VerifyCode;

